import React, { useEffect, useRef, useState } from "react"
import styled, { keyframes } from "styled-components"
import { AGE_GATE_DAYS, AGE_GATE_KEY, isBot, needsAgeGate } from "../../utils/ageGate"
import { trackAgeGate } from "../../utils/analytics"

// 年齢確認。初めての訪問（と AGE_GATE_DAYS 日ごと）に、ブラウザでだけ出す。「18歳未満です」はサイトを出る。
// React が動く前は gatsby-ssr.js の覆い（AGE_GATE_STYLE）が画面を隠し、これが出たら同じ見た目の自分の背景に替わる。

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: none; }
`

const Gate = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147483001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(13, 17, 22, 0.88);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  .card {
    width: 100%;
    max-width: 440px;
    padding: 32px 28px 24px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-lg);
    background: var(--surface-solid);
    box-shadow: var(--shadow-lg);
    text-align: center;
    animation: ${rise} 0.35s var(--ease) both;
  }

  .mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    border: 2px solid var(--accent-pink);
    border-radius: 50%;
    color: var(--accent-pink);
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  h2 {
    margin: 0 0 12px;
    color: var(--text-strong);
    font-size: 20px;
    font-weight: 700;
  }

  p {
    margin: 0 0 8px;
    color: var(--text);
    font-size: 14px;
    line-height: 1.8;
  }

  .note {
    color: var(--text-faint);
    font-size: 12px;
  }

  .actions {
    display: grid;
    gap: 10px;
    margin-top: 20px;
  }

  button {
    width: 100%;
    min-height: 48px;
    border-radius: var(--radius-md);
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s var(--ease), border-color 0.2s var(--ease), color 0.2s var(--ease);
  }

  .enter {
    border: 1px solid var(--accent);
    background: var(--accent);
    color: #fff;

    &:hover,
    &:focus-visible {
      background: var(--accent-hover);
    }
  }

  .leave {
    border: 1px solid var(--border-strong);
    background: transparent;
    color: var(--text-muted);

    &:hover,
    &:focus-visible {
      border-color: var(--text-muted);
      color: var(--text);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card {
      animation: none;
    }
  }

  @media (max-width: 576px) {
    .card {
      padding: 28px 20px 20px;
    }
  }
`

// wrapRootElement は Head の描画にも使われるので、AgeGate は同時に2つ動く。表示の計測は1回だけ送る
let viewTracked = false

const AgeGate = () => {
  const [open, setOpen] = useState(false)
  const enterRef = useRef(null)

  useEffect(() => {
    if (!isBot(navigator.userAgent) && needsAgeGate()) {
      setOpen(true)
      if (!viewTracked) {
        viewTracked = true
        trackAgeGate("view")
      }
    } else document.documentElement.removeAttribute("data-age-gate")
  }, [])

  useEffect(() => {
    if (!open) return undefined
    // 覆いをこの背景に替え、後ろのページはスクロールさせない
    document.documentElement.removeAttribute("data-age-gate")
    const overflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    enterRef.current?.focus()
    return () => {
      document.body.style.overflow = overflow
    }
  }, [open])

  const enter = () => {
    trackAgeGate("enter")
    try {
      window.localStorage.setItem(AGE_GATE_KEY, String(Date.now()))
    } catch {
      // 保存できなくても、このタブのあいだは閉じたままにする
    }
    setOpen(false)
  }

  const leave = () => {
    const go = () => window.location.replace("https://www.google.co.jp/")
    // すぐ離れると GA へ届かないので、送り終わるのを待つ（広告ブロッカーなどで返事が来なくても 300ms で離れる）
    trackAgeGate("leave", go)
    setTimeout(go, 300)
  }

  if (!open) return null
  return (
    <Gate role="dialog" aria-modal="true" aria-labelledby="age-gate-title" aria-describedby="age-gate-text">
      <div className="card">
        <span className="mark" aria-hidden="true">
          18+
        </span>
        <h2 id="age-gate-title">年齢確認</h2>
        <p id="age-gate-text">
          どうじんしらべは、FANZA同人など成人向けの作品の情報を扱うサイトです。18歳未満の方はご利用いただけません。
        </p>
        <p className="note">「18歳以上です」を押すと、{AGE_GATE_DAYS}日間はこの確認を出しません。</p>
        <div className="actions">
          <button ref={enterRef} type="button" className="enter" onClick={enter}>
            18歳以上です（入る）
          </button>
          <button type="button" className="leave" onClick={leave}>
            18歳未満です（出る）
          </button>
        </div>
      </div>
    </Gate>
  )
}

export default AgeGate
